import { Assumption } from '@portal/pkg-api/gen/navigator'
import { AssumptionApi } from '@portal/pkg-api/navigator'
import { useQuery } from '@tanstack/react-query'
import i18n, { ParseKeys } from 'i18next'
import { data } from '@portal/pkg-api/shared'

const prefix = 'admin.assumptions'

export const AssumptionQueries = {
  selectOptionsFilterable: (options: SelectOptionsFilterableOptions) => ({
    queryKey: [`${prefix}.selectOptionsFilterable`, options],
    queryFn: selectOptionsFilterableQueryFn(options),
  }),
  creationAssumptions: (entityTypeId?: number) => ({
    queryKey: [`${prefix}.creationAssumptions`, entityTypeId],
    queryFn: async () => {
      return await data(AssumptionApi.getCreationAssumptions(entityTypeId!))
    },
    enabled: !!entityTypeId,
  }),
}

const selectOptionsFilterableQueryFn =
  ({
    onlyFilterable,
    includeNull,
    excludePseudo,
    entityTypeId,
    includedAssumptionTypes,
    excludedAssumptionTypes,
    appendRefNameLabel,
  }: SelectOptionsFilterableOptions) =>
  async () => {
    const as = entityTypeId
      ? await AssumptionApi.getAssumptions(entityTypeId)
      : await AssumptionApi.getAllAssumptions()

    const options: AutocompleteOption[] = as.data
      ? as.data
          .filter((of) => {
            return onlyFilterable ? of.filterable : true
          })
          .filter((at) => {
            return includedAssumptionTypes && includedAssumptionTypes.length
              ? includedAssumptionTypes.includes(at.type)
              : true
          })
          .filter((at) => {
            return excludedAssumptionTypes && excludedAssumptionTypes.length
              ? !excludedAssumptionTypes.includes(at.type)
              : true
          })
          .filter((ps) => {
            return excludePseudo ? !ps.pseudo : true
          })
          .map((fa: Assumption) => {
            return appendRefNameLabel
              ? {
                  label: i18n.t(fa.label as ParseKeys) + ' (' + fa.refName + ')',
                  id: fa.id,
                }
              : {
                  label: i18n.t(fa.label as ParseKeys),
                  id: fa.id,
                }
          })
      : []
    return includeNull ? [nullOption, ...options] : options
  }

export const nullOption = { label: 'None', id: -1 }

export type AutocompleteOption = {
  label: string
  id: number
}

type SelectOptionsFilterableOptions = {
  onlyFilterable: boolean
  includeNull: boolean
  excludePseudo: boolean
  entityTypeId?: number
  includedAssumptionTypes?: string[]
  excludedAssumptionTypes?: string[]
  appendRefNameLabel?: boolean
}

export function useAssumptionsByEntityTypeMaybeForSelectOptions(
  onlyFilterable: boolean,
  includeNull: boolean,
  excludePseudo: boolean,
  entityTypeId?: number,
  includedAssumptionTypes?: string[],
  excludedAssumptionTypes?: string[],
  appendRefNameLabel?: boolean,
) {
  return useQuery(
    AssumptionQueries.selectOptionsFilterable({
      onlyFilterable,
      includeNull,
      excludePseudo,
      entityTypeId,
      includedAssumptionTypes,
      excludedAssumptionTypes,
      appendRefNameLabel,
    }),
  )
}
